import { Linkedin, Lock, Youtube } from "lucide-react";
import { getComplianceCoreUrl } from "@/config/domains";
import { useTranslation } from "react-i18next";
import LocaleLink from "@/components/LocaleLink";
import FooterLocaleSwitcher from "@/components/FooterLocaleSwitcher";
import MegaFooter from "@/components/footer/MegaFooter";
import { CTA_ROW } from '@/lib/cta-row';

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-brand-ink text-brand-ink-foreground" role="contentinfo">
      {/* Top CTA strip */}
      <div className="bg-brand-ink-foreground/5 border-b border-brand-ink-foreground/10">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-xl font-bold mb-2 md:text-2xl">{t('footer.ctaTitle')}</h2>
              <p className="text-brand-ink-foreground/90">{t('footer.ctaSubtitle')}</p>
            </div>
            <div className={`shrink-0 ${CTA_ROW}`}>
              <LocaleLink to="/for/saas" className="w-full inline-flex items-center justify-center gap-2 bg-white text-brand-ink hover:bg-white/90 font-semibold px-6 py-3 rounded-lg transition-colors">
                {t('footer.getStartedFree')}
              </LocaleLink>
              <LocaleLink to="/book-demo" className="w-full inline-flex items-center justify-center gap-2 border border-brand-ink-foreground/30 hover:bg-brand-ink-foreground/10 font-semibold px-6 py-3 rounded-lg transition-colors">
                {t('footer.bookDemo')}
              </LocaleLink>
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="border-b border-brand-ink-foreground/10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-center md:justify-between gap-4 text-sm text-brand-ink-foreground/85">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <LocaleLink to="/trust" className="hover:text-brand-ink-foreground font-medium">
              {t('footer.trustCenter')}
            </LocaleLink>
            <span className="opacity-40">·</span>
            <LocaleLink to="/data-security" className="hover:text-brand-ink-foreground">
              {t('footer.dataSecurity')}
            </LocaleLink>
            <span className="opacity-40">·</span>
            <LocaleLink to="/privacy-policy" className="hover:text-brand-ink-foreground">
              {t('footer.privacyPolicy')}
            </LocaleLink>
          </div>
          <div className="text-xs opacity-80">
            {t('megaFooter.trustStrip')}
          </div>
        </div>
      </div>

      {/* Mega-footer */}
      <div className="container mx-auto px-6 py-12">
        <MegaFooter />
      </div>

      {/* Brand + legal row */}
      <div className="border-t border-brand-ink-foreground/10">
        <div className="container mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            <div>
              <LocaleLink to="/" className="flex items-center mb-3 hover:opacity-80 transition-opacity">
                <img src="/assets/logos/csquare_logo_horizontal_white_sm.webp" alt="cSquare GRC - Autonomous Compliance" className="h-10 w-auto" width={140} height={56} loading="lazy" />
              </LocaleLink>
              <p className="text-brand-ink-foreground/80 text-sm leading-relaxed max-w-xs">
                {t('footer.tagline')}
              </p>
              <div className="flex items-center space-x-3 mt-3">
                <a href="https://www.linkedin.com/company/csquare-platform/" target="_blank" rel="noopener noreferrer" className="text-brand-ink-foreground/70 hover:text-brand-ink-foreground transition-colors" aria-label="LinkedIn">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://www.youtube.com/@csquaregrc" target="_blank" rel="noopener noreferrer" className="text-brand-ink-foreground/70 hover:text-brand-ink-foreground transition-colors" aria-label="YouTube">
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-brand-ink-foreground/80 md:col-span-2 md:justify-self-end">
              <li><LocaleLink to="/privacy-policy" className="hover:text-brand-ink-foreground transition-colors">{t('footer.privacyPolicy')}</LocaleLink></li>
              <li><LocaleLink to="/cookie-policy" className="hover:text-brand-ink-foreground transition-colors">{t('footer.cookiePolicy')}</LocaleLink></li>
              <li><LocaleLink to="/terms" className="hover:text-brand-ink-foreground transition-colors">{t('footer.termsOfService')}</LocaleLink></li>
              <li><LocaleLink to="/dsar" className="hover:text-brand-ink-foreground transition-colors">{t('footer.privacyRequest')}</LocaleLink></li>
              <li><LocaleLink to="/do-not-sell" className="hover:text-brand-ink-foreground transition-colors">{t('footer.doNotSell')}</LocaleLink></li>
              <li><a href={getComplianceCoreUrl("/auth")} className="hover:text-brand-ink-foreground transition-colors">{t('footer.signIn')}</a></li>
            </ul>
          </div>
          <p className="text-sm text-brand-ink-foreground/70 mt-6">{t('footer.copyright')}</p>
          <FooterLocaleSwitcher />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
